/** The shape `Promise.try` has in the standard, for a runtime whose `Promise` is missing it. */
type PromiseTry = <TResult, TArguments extends Array<unknown>>(
  callback: (...args: TArguments) => TResult | PromiseLike<TResult>,
  ...args: TArguments
) => Promise<Awaited<TResult>>;

type PromiseMaybeWithTry = PromiseConstructor & { try?: PromiseTry };

/**
 * Puts `Promise.try` back when the page is missing it.
 *
 * PDF.js calls it while talking to its worker. Angular replaces the global `Promise` with zone.js's
 * own, which implements `all`, `race`, `resolve` and `reject` and nothing newer -- so the browser's
 * `Promise.try` is hidden behind it, the call throws inside a worker message handler, and the
 * document never finishes opening. Patching the global is not something a library should do lightly;
 * it is done here because this is where the breakage is understood, and the alternative is for every
 * host application to know about it.
 */
export function installPromiseTry(): void {
  const constructor = Promise as PromiseMaybeWithTry;

  if (typeof constructor.try === 'function') return;

  // A callback that throws rather than returns must reject, which is what the executor does for us.
  const attempt = (callback: (...args: Array<unknown>) => unknown, ...args: Array<unknown>): Promise<unknown> =>
    new Promise<unknown>((resolve) => resolve(callback(...args)));

  constructor.try = attempt as PromiseTry;
}
